import React, {createContext, useContext, useState} from "react";
import {Layout} from "../types/utils";

type Theme = 'light' | 'dark';
type Visual = 'modern' | 'classic';

interface AppContextValues {
    blur: any
    active: undefined | null | string
    sidebar: boolean
    sidebarWidth: number
    modalWidth: number
    setActive: React.Dispatch<React.SetStateAction<undefined | null | string>>
    setModalWidth: React.Dispatch<React.SetStateAction<number>>
    toggleSidebar: React.Dispatch<React.SetStateAction<boolean>>
    theme: Theme
    setTheme: React.Dispatch<React.SetStateAction<Theme>>
    uiVisual: Visual
    setVisual: React.Dispatch<React.SetStateAction<Visual>>
}

const resolveStoredTheme = (): Theme => {
    const theme = localStorage.getItem('theme');
    if (theme === 'light' || theme === 'dark') {
        return theme;
    }

    return 'light';
};

const resolveStoredVisual = (): Visual => {
    const visual = localStorage.getItem('visual');
    if (visual === 'classic' || visual === 'modern') {
        return visual;
    }

    return 'modern';
};

const AppContext = createContext<AppContextValues>({
    blur: {},
    active: undefined,
    sidebar: true,
    sidebarWidth: 260,
    modalWidth: 380,
    setActive: (): void => {},
    setModalWidth: (): void => {},
    toggleSidebar: (): void => {},
    theme: resolveStoredTheme() || window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
    setTheme: (): void => {},
    uiVisual: resolveStoredVisual(),
    setVisual: (): void => {}
});

const AppContextProvider: React.FC<Layout> = ({children}) => {
    const [active, setActive] = useState<undefined | null | string>(undefined);
    const [sidebar, toggleSidebar] = useState<boolean>(true);
    const [sidebarWidth] = useState<number>(260);
    const [modalWidth, setModalWidth] = useState<number>(380);
    const [theme, setTheme] = useState(resolveStoredTheme());
    const [uiVisual, setVisual] = useState(resolveStoredVisual());

    const blur = {}

    const context = {
        active,
        setActive,
        sidebar,
        sidebarWidth,
        toggleSidebar,
        modalWidth,
        setModalWidth,
        blur,
        theme,
        setTheme,
        uiVisual,
        setVisual
    }

    return (
        <AppContext.Provider value={context}>
            {children}
        </AppContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppContext = () => {
    return useContext(AppContext);
};

export default AppContextProvider;
